import useSWRImmutable from "swr/immutable";
import { useState } from "react";
import { fetcher } from "../../../util/request";
import {
  Row,
  Col,
  Form,
  Select,
  Input,
  Space,
  Button,
  Typography,
  Tag,
  Table,
  TableColumnsType,
  Card,
  Switch,
  Modal,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import axios from "axios";
import Loading from "../../../components/wrappers/Loading";

type AddContributorForm = Pick<Contributor, "email" | "name">;

interface UserCreationInputProps {
  refresh: () => void;
}

const UserCreationInput = ({ refresh }: UserCreationInputProps) => {
  const [form] = Form.useForm<AddContributorForm>();

  const handleSubmit = async () => {
    await axios.post("/admin/contributor/create", {
      email: `${form.getFieldValue("email")}@accessabroadhk.org`,
      name: form.getFieldValue("name"),
    });
    form.resetFields();
    refresh();
  };

  return (
    <Card title="Add new contributor">
      <Form
        form={form}
        name="create-new-contributor"
        layout="horizontal"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 20 }}
        colon={false}
        onFinish={handleSubmit}
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, message: "Email is required" }]}
        >
          <Input addonAfter={<Typography>@accessabroadhk.org</Typography>} />
        </Form.Item>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Create
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

type PermissionGroup = string;

interface PermissionGroupListProps {
  groups: PermissionGroup[];
  refresh: () => void;
}

const PermissionGroupList = ({ groups, refresh }: PermissionGroupListProps) => {
  const [form] = Form.useForm();

  const handleClose = async (removedGroup: PermissionGroup) => {
    await axios
      .delete(`/admin/permission-group/${removedGroup}`)
      .then((response) => refresh());
  };

  const handleCreate = async () => {
    await axios
      .post(
        `/admin/permission-group/${form.getFieldValue("group-name")}/create`,
      )
      .then((res) => refresh());
    form.resetFields();
  };

  return (
    <Card title="Manage permission groups">
      <Row gutter={[16, 16]}>
        <Col>
          <Space wrap>
            {groups?.sort().map((group) => (
              <Tag
                closable={true}
                onClose={() => handleClose(group)}
                key={group}
              >
                {group}
              </Tag>
            ))}
          </Space>
        </Col>
        <Col>
          <Form form={form} layout="inline" onFinish={handleCreate}>
            <Form.Item
              name="group-name"
              rules={[{ required: true, message: "Group name is required" }]}
            >
              <Space.Compact>
                <Input placeholder="New group name" />
                <Button type="primary" htmlType="submit">
                  Create
                </Button>
              </Space.Compact>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Card>
  );
};

interface Contributor {
  id: number;
  email: string;
  name: string;
  groups: string[];
  disabled: boolean;
}

type EditContributorForm = Pick<Contributor, "name" | "groups" | "disabled">;

const UserList = () => {
  const {
    data: contributors,
    mutate: refreshContributors,
    isLoading: loadingContributors,
  } = useSWRImmutable<Contributor[]>(`/admin/contributor/list`, fetcher);

  const {
    data: permissionGroups,
    mutate: refreshPermissionGroups,
    isLoading: loadingGroups,
  } = useSWRImmutable<PermissionGroup[]>(
    `/admin/permission-group/list`,
    fetcher,
  );

  const [form] = Form.useForm<EditContributorForm>();
  const [show, setShow] = useState(false);

  const handleEdit = (record: Contributor) => {
    form.setFieldsValue({ ...record });
    setShow(true);
  };

  const handleSubmit = async () => {
    await axios
      .patch(`/admin/contributor/${form.getFieldValue("id")}/patch/`, {
        name: form.getFieldValue("name"),
        groups: form.getFieldValue("groups"),
        disabled: form.getFieldValue("disabled"),
      })
      .then(() => {
        refreshContributors();
        form.resetFields();
      });
    setShow(false);
  };

  const columns: TableColumnsType<Contributor> = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => {
        const emailA = a.email?.replace("@accessabroadhk.org", "");
        const emailB = b.email?.replace("@accessabroadhk.org", "");
        if (emailA < emailB) return -1;
        if (emailA > emailB) return 1;
        return 0;
      },
      render: (text) => text?.replace("@accessabroadhk.org", ""),
      width: "15%",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      filterSearch: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search..."
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={(e) => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={(e) => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              setSelectedKeys([]);
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) =>
        record.name.toLowerCase().includes((value as string).toLowerCase()),
      width: "15%",
    },
    {
      title: "Groups",
      dataIndex: "groups",
      key: "groups",
      filters: permissionGroups?.sort().map((group) => {
        return {
          text: group,
          value: group,
        };
      }),
      onFilter: (value, record) => record.groups.includes(value as string),
      render: (groups) => (
        <div className="flex flex-wrap gap-1">
          {groups.sort().map((group) => (
            <Tag key={group}>{group}</Tag>
          ))}
        </div>
      ),
      width: "40%",
    },
    {
      title: "Disabled",
      dataIndex: "disabled",
      key: "disabled",
      filters: [
        { text: "Enabled", value: false },
        { text: "Disabled", value: true },
      ],
      onFilter: (value, record) => record.disabled === value,
      render: (disabled) => (
        <Tag color={disabled ? "red" : "green"}>
          {disabled ? "Disabled" : "Enabled"}
        </Tag>
      ),
      width: "15%",
    },
    {
      title: "Edit",
      render: (_, record) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
      ),
      width: "15%",
    },
  ];

  return (
    <Loading loading={loadingContributors || loadingGroups}>
      <div className="mx-3 mt-3">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} xl={8}>
            <UserCreationInput refresh={refreshContributors} />
          </Col>
          <Col xs={24} md={12} xl={8}>
            <PermissionGroupList
              groups={permissionGroups}
              refresh={refreshPermissionGroups}
            />
          </Col>
          <Col span={24}>
            <Table
              columns={columns}
              dataSource={contributors}
              pagination={false}
              size="middle"
              scroll={{ x: true }}
              sticky={true}
            />
          </Col>
        </Row>
        <Modal
          title={"Edit Contributor - " + form.getFieldValue("email")}
          open={show}
          onCancel={() => {
            form.resetFields();
            setShow(false);
          }}
          onOk={form.submit}
          okText="Save"
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Groups" name="groups">
              <Select
                mode="multiple"
                placeholder={"Add permission groups"}
                options={permissionGroups?.sort()?.map((group) => ({
                  value: group,
                  label: group,
                }))}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item label="Disabled" name="disabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Loading>
  );
};

export default UserList;
